import type { ChatMessage, CurrentConversation } from '../types/chat.types';
import type { PredefinedPromptProps } from '../types/predefinedPrompts';

const STORAGE_KEYS = {
  CONVERSATION: 'developer-portal-chat-history',
} as const;

const CONVERSATION_EXPIRY_HOURS = 48;

const isConversationExpired = (updatedAt: string): boolean => {
  try {
    const updatedDate = new Date(updatedAt);
    const now = new Date();
    const diffHours =
      (now.getTime() - updatedDate.getTime()) / (1000 * 60 * 60);
    return diffHours > CONVERSATION_EXPIRY_HOURS;
  } catch {
    return true;
  }
};

const deleteReasoningFromMessages = (messages: ChatMessage[]): ChatMessage[] =>
  messages.map(message => {
    const persistedMessage = { ...message };
    delete persistedMessage.reasoningSummary;
    return persistedMessage;
  });

export const clearConversationFromStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.CONVERSATION);
  } catch {
    // Silent fail for storage errors
  }
};

const getCurrentConversation = (): CurrentConversation | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CONVERSATION);

    if (!stored) {
      return null;
    }

    const parsed: CurrentConversation = JSON.parse(stored);

    if (isConversationExpired(parsed.updatedAt)) {
      clearConversationFromStorage();
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const loadMessages = (): ChatMessage[] => {
  const conversation = getCurrentConversation();
  return conversation?.messages || [];
};

export const loadConversationId = (): string => {
  const conversation = getCurrentConversation();
  return conversation?.id || '';
};

export const loadResponseId = (): string | null => {
  const conversation = getCurrentConversation();
  return conversation?.responseId || null;
};

export const loadPredefinedPromptProps = (): PredefinedPromptProps | null => {
  const conversation = getCurrentConversation();
  return conversation?.predefinedPromptProps || null;
};

export const saveConversationToStorage = (
  messages: ChatMessage[],
  conversationId: string,
  responseId?: string | null,
  predefinedPromptProps?: PredefinedPromptProps | null,
): void => {
  if (messages.length === 0) return;

  try {
    const conversation: CurrentConversation = {
      id: conversationId,
      messages: deleteReasoningFromMessages(messages),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      responseId: responseId || undefined,
      predefinedPromptProps: predefinedPromptProps || undefined,
    };

    localStorage.setItem(
      STORAGE_KEYS.CONVERSATION,
      JSON.stringify(conversation),
    );
  } catch {
    // Silent fail for storage errors
  }
};
